import React from 'react';
import { BookOpen, Lightbulb, ExternalLink, Plus } from 'lucide-react';
import { usePreview } from '../../context/PreviewContext';
import { Button } from '../../components/ui/button';

/**
 * Preview Learn & Notes Page
 * Read-only ideas/notes with mock data
 */
const PreviewLearnNotesPage = () => {
  const { ideas } = usePreview();

  const getCategoryColor = (category) => {
    switch (category) {
      case 'question': return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
      case 'research': return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' };
      case 'lifestyle': return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
      default: return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' };
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'question': return 'Question for Doctor';
      case 'research': return 'Research Note';
      case 'lifestyle': return 'Lifestyle Idea';
      default: return 'Note';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Learn & Notes</h1>
          <p className="text-slate-500 mt-1">Save ideas and questions as you explore</p>
        </div>
        <Button disabled className="opacity-50 cursor-not-allowed">
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>

      {/* Intro */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/80 rounded-xl">
            <Lightbulb className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Your Personal Knowledge Base</h2>
            <p className="text-slate-600">
              Save insights, questions for your doctor, and research notes as you browse 
              the app. Build your own collection of CLL knowledge tailored to your journey.
            </p>
          </div>
        </div>
      </div>

      {/* Ideas Grid */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Saved Ideas ({ideas.length})</h3>
        
        {ideas.map(idea => {
          const colors = getCategoryColor(idea.category);
          
          return (
            <div 
              key={idea.id}
              className={`bg-white rounded-xl border ${colors.border} p-5`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 ${colors.bg} rounded-lg`}>
                    <BookOpen className={`h-5 w-5 ${colors.text}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{idea.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs font-medium ${colors.text} ${colors.bg} px-2 py-0.5 rounded-full`}>
                        {getCategoryLabel(idea.category)}
                      </span>
                      <span className="text-xs text-slate-400">
                        from {idea.source_label}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-xs text-slate-400">
                  {new Date(idea.saved_at).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              <p className="text-slate-600 text-sm leading-relaxed">
                {idea.note}
              </p>
              
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
                <button 
                  disabled
                  className="text-sm text-slate-400 cursor-not-allowed flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Source
                </button>
                <button 
                  disabled
                  className="text-sm text-slate-400 cursor-not-allowed"
                >
                  Edit
                </button>
                <button 
                  disabled
                  className="text-sm text-slate-400 cursor-not-allowed"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State Info */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
        <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">
          In the full app, you can save ideas from any page by clicking the "Save to Ideas" button.
        </p>
      </div>
    </div>
  );
};

export default PreviewLearnNotesPage;
